import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { useNotification } from "../../shared/NotificationProvider";
import { NotificationPermissionButton } from "../../../components/NotificationPermissionButton";
import {
  loadBrowserNotifyEventPrefs,
  saveBrowserNotifyEventPrefs,
  type BrowserNotifyEventPrefs
} from "../../../constants/browserNotifications";

export const BrowserNotificationsData = () => {
  const { permission, isSupported } = useNotification();
  const [status, setStatus] = useState<string>("");
  const [eventPrefs, setEventPrefs] = useState<BrowserNotifyEventPrefs>(() =>
    loadBrowserNotifyEventPrefs()
  );

  useEffect(() => {
    setEventPrefs(loadBrowserNotifyEventPrefs());
  }, [permission]);

  useEffect(() => {
    if (!isSupported) {
      setStatus("not-supported");
    } else if (permission === "granted") {
      setStatus("granted");
    } else if (permission === "denied") {
      setStatus("denied");
    } else {
      setStatus("default");
    }
  }, [permission, isSupported]);

  const togglePref = useCallback(
    (key: keyof BrowserNotifyEventPrefs, checked: boolean) => {
      const next: BrowserNotifyEventPrefs = {
        ...eventPrefs,
        [key]: checked
      };
      setEventPrefs(next);
      saveBrowserNotifyEventPrefs(next);
    },
    [eventPrefs]
  );

  const getStatusMessage = useCallback(() => {
    switch (status) {
      case "granted":
        return (
          <div className="alert alert-success mb-0">
            <i className="fa fa-check-circle me-2"></i>
            {TranslationService.translate?.NotificationsEnabled ||
              "Las notificaciones del navegador están habilitadas. Recibirás notificaciones incluso cuando estés en otra pestaña."}
          </div>
        );
      case "denied":
        return (
          <div className="alert alert-warning mb-0">
            <i className="fa fa-exclamation-triangle me-2"></i>
            {TranslationService.translate?.NotificationsDenied ||
              "Las notificaciones están bloqueadas. Por favor, habilítalas manualmente en la configuración de tu navegador."}
          </div>
        );
      case "not-supported":
        return (
          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            {TranslationService.translate?.NotificationsNotSupported ||
              "Tu navegador no soporta notificaciones del navegador."}
          </div>
        );
      default:
        return (
          <div className="alert alert-info mb-0">
            <i className="fa fa-bell me-2"></i>
            {TranslationService.translate?.NotificationsNotEnabled ||
              "Las notificaciones del navegador no están habilitadas. Haz clic en el botón para habilitarlas."}
          </div>
        );
    }
  }, [status]);

  if (!isSupported) {
    return null;
  }

  return (
    <div className="card-body">
      <div className="mb-2 mt-3">
        <h5>
          {TranslationService.translate?.BrowserNotifications ||
            "Notificaciones del Navegador"}
        </h5>
      </div>
      <div className="col-12 mb-3">
        <p className="text-muted">
          {TranslationService.translate?.BrowserNotificationsDescription ||
            "Recibe notificaciones emergentes incluso cuando estés en otra pestaña o aplicación. Estas notificaciones te mantendrán informado sobre nuevas llamadas, mensajes y actualizaciones importantes."}
        </p>
      </div>
      <div className="col-12 mb-3">{getStatusMessage()}</div>

      {status === "granted" && (
        <div className="col-12 mb-3">
          <h6 className="text-muted small text-uppercase mb-2">
            {TranslationService.translate?.BrowserNotifyEventsTitle ||
              "Eventos del sistema"}
          </h6>
          <div className="form-check align-items-center">
            <input
              className="form-check-input me-2"
              type="checkbox"
              id="browserNotify-whatsappIncoming"
              checked={eventPrefs.whatsappIncoming}
              onChange={(e) => togglePref("whatsappIncoming", e.target.checked)}
            />
            <label
              className="form-check-label mt-1"
              htmlFor="browserNotify-whatsappIncoming"
            >
              {TranslationService.translate?.BrowserNotifyWhatsAppIncomingLabel}
            </label>
          </div>
          <p className="text-muted small mt-2 mb-0">
            {TranslationService.translate?.BrowserNotifyEventsHint}
          </p>
        </div>
      )}

      {status !== "granted" && status !== "denied" && (
        <div className="col-12">
          <NotificationPermissionButton
            variant="button"
            onPermissionGranted={() => {
              setStatus("granted");
            }}
            onPermissionDenied={() => {
              setStatus("denied");
            }}
          />
        </div>
      )}
    </div>
  );
};
